"use client";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { WeaponDetail } from "@/types/weapon";

interface AttachmentsListProps {
  attachments: WeaponDetail["attachments"];
}

export function AttachmentsList({ attachments }: AttachmentsListProps) {
  return (
    <div className="w-full border-[#262A2D] border-t bg-[#101214] md:w-1/3 md:border-t-0 md:border-l">
      <div className="border-[#262A2D] border-b p-4">
        <span className="font-mono text-[#F5A23E] text-[11px] tracking-[0.2em]">
          {`// ATTACHMENTS · ${attachments.length}`}
        </span>
      </div>

      <div className="flex flex-col">
        {attachments.length === 0 ? (
          <div className="p-4 text-center text-[#6a7075] text-sm">
            No attachments equipped
          </div>
        ) : (
          attachments.map((attachment) => (
            <Tooltip key={attachment._id}>
              <TooltipTrigger asChild>
                <div className="flex cursor-pointer items-center justify-between border-[#1c1f22] border-b p-3.5 transition-colors hover:bg-[#141618]">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[#46C8E0] text-[9px] tracking-[0.15em]">
                      {attachment.slotType.toUpperCase()}
                    </div>
                    <h4 className="mt-0.5 truncate font-semibold text-[#EAEBEC] text-sm">
                      {attachment.name}
                    </h4>
                  </div>
                  <span className="shrink-0 font-bold font-mono text-[#F5A23E] text-sm">
                    {attachment.cost}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent
                className="max-w-xs border border-[#F5A23E]/30 bg-[#0a0b0c]/90 backdrop-blur-sm"
                side="left"
                sideOffset={8}
              >
                <div className="space-y-2">
                  <div className="font-bold font-mono text-sm text-white">
                    {attachment.name.toUpperCase()}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-[#46C8E0]">
                      {attachment.slotType.toUpperCase()}
                    </span>
                    <span className="text-white/60">•</span>
                    <span className="font-bold font-mono text-[#F5A23E]">
                      {attachment.cost} PTS
                    </span>
                  </div>

                  {/* Complete Stats Display */}
                  <div className="space-y-1">
                    <div className="font-mono font-semibold text-white/80 text-xs">
                      STAT MODIFICATIONS:
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      {Object.entries(attachment.stats).map(([stat, value]) => {
                        if (value === 0) {
                          return null;
                        }
                        const isPositive = value > 0;
                        return (
                          <div className="flex justify-between" key={stat}>
                            <span className="font-mono text-white/70 capitalize">
                              {stat}:
                            </span>
                            <span
                              className={`font-bold font-mono ${
                                isPositive ? "text-green-400" : "text-red-400"
                              }`}
                            >
                              {isPositive ? "+" : ""}
                              {value}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {Object.values(attachment.stats).every(
                      (value) => value === 0
                    ) && (
                      <div className="font-mono text-white/50 text-xs italic">
                        No stat modifications
                      </div>
                    )}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          ))
        )}
      </div>
    </div>
  );
}
